import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

//: Cermin `URUT_SAH_TEMUAN` di backend/routers/papan_kontrol.py.
const URUT_SAH_TEMUAN = ["terdeteksi_at", "warna", "jenis_temuan", "status"];
const STATUS_TEMUAN = { TERBUKA: "Terbuka", DITUTUP: "Ditutup" };
const WARNA_TEMUAN = { MERAH: "Merah", KUNING: "Kuning" };

// K180 — Papan Kontrol v1: PENYIMPANGAN PROSEDUR & DATA (bukan keterlambatan —
// itu alarm produksi; bukan pajangan — itu Papan TV). Temuan tidak pernah
// dihapus, hanya DITUTUP dengan alasan dari kosakata tertutup (Q29) oleh
// Kepala Cabang (regionnya) / Owner.
const LABEL_JENIS = {
  K13_TARIF_DIUBAH_SESUDAH_SERAH_TERIMA: "Tarif borongan diubah sesudah serah terima",
  Q20_SELISIH_SERAH_TERIMA: "Selisih serah terima antar tahap",
  Q21_WO_MENUNGGU_REMAKE: "WO berhenti menunggu remake",
  K108_UPDATE_LEWAT_TUTUP_HARI: "Update lewat tutup hari kerja",
  DOKU_TAUTAN_KEDALUWARSA: "Tautan bayar DOKU kedaluwarsa",
  BACKUP_TUGAS_TERJADWAL_GAGAL: "Backup / tugas terjadwal gagal",
  Q42_FA_LAMA_BERISIKO_TELAT: "FA lama berisiko telat",
  TARIF_BORONGAN_BELUM_LENGKAP: "Tarif borongan belum lengkap",
};

export default function PapanKontrol() {
  const { isOwner, isKepalaCabang, profile } = useAuth();
  const bolehTutup = isOwner || isKepalaCabang;
  const [data, setData] = useState({ items: [], total: 0, terbuka_merah: 0, terbuka_kuning: 0 });
  const [alasan, setAlasan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tutup, setTutup] = useState(null);
  const [memeriksa, setMemeriksa] = useState(false);
  // U24b (K158): ringkasan harian per region + PDF berumur (K164 b5).
  const [ringkasan, setRingkasan] = useState({ items: [], umur_pdf_hari: null });

  // Daftar temuan — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
  // Temuan tidak pernah dihapus, jadi tabelnya terus tumbuh: halaman, cari ringkasan,
  // urut, dan saringan dikerjakan server. Saringan bawaan: status Terbuka (chip, bisa
  // dihapus). Angka MERAH/KUNING di kepala = seluruh temuan TERBUKA di cakupan Anda,
  // dihitung server — tidak ikut saringan dan tidak ikut halaman.
  const daftar = useDaftar(data.items, {
    server: { total: data.total ?? 0, urutSah: URUT_SAH_TEMUAN },
    urutAwal: { kunci: "warna", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "TERBUKA", tampil: (v) => STATUS_TEMUAN[v] || v },
      warna: { label: "Warna", param: "warna", tampil: (v) => WARNA_TEMUAN[v] || v },
      jenis: { label: "Jenis", param: "jenis", tampil: (v) => LABEL_JENIS[v] || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const status = daftar.nilaiSaringan.status;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    // Status dihapus orang = semua status (tanpa parameter, server memakai TERBUKA).
    status: paramSaringan.status || "SEMUA",
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/papan-kontrol/temuan", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, r] = await Promise.all([
        api.get("/papan-kontrol/temuan", { params: bangunParams() }),
        api.get("/papan-kontrol/alasan"),
        api.get("/papan-kontrol/ringkasan", { params: { limit: 14 } }),
      ]);
      setData(t.data); setAlasan(a.data.items || []); setRingkasan(r.data || { items: [] });
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat Papan Kontrol")); }
    finally { setLoading(false); }
  }, [bangunParams]);
  useEffect(() => { load(); }, [load]);

  const labelAlasan = (t) => alasan.find((a) => a.code === t.alasan_tutup_code)?.label_id || "—";
  const KOLOM_CSV = [
    { kunci: "warna", judul: "Warna", ambil: (t) => WARNA_TEMUAN[t.warna] || t.warna },
    { kunci: "jenis_temuan", judul: "Jenis", ambil: (t) => LABEL_JENIS[t.jenis_temuan] || t.jenis_temuan },
    { kunci: "ringkasan", judul: "Ringkasan" },
    { kunci: "terdeteksi_at", judul: "Terdeteksi", ambil: (t) => formatDateTime(t.terdeteksi_at) },
    { kunci: "status", judul: "Status", ambil: (t) => (t.status === "DITUTUP" ? `Ditutup · ${labelAlasan(t)}` : "Terbuka") },
    { kunci: "catatan_tutup", judul: "Catatan tutup", ambil: (t) => t.catatan_tutup || "" },
  ];

  const periksaSekarang = async () => {
    setMemeriksa(true);
    try {
      const r = await api.post("/papan-kontrol/periksa", { region_id: profile?.region_id || null });
      const d = r.data;
      toast.success(d.per_region ? `Diperiksa: ${Object.entries(d.per_region).map(([k, v]) => `${k}: ${v}`).join(" · ")}`
        : `Kandidat ${d.kandidat}, baru ${d.baru.length}, diperbarui ${d.diperbarui.length}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Pemeriksaan gagal")); }
    finally { setMemeriksa(false); }
  };

  const bukaPdf = async (docId) => {
    try {
      const r = await api.get(`/pdf/documents/${docId}/signed-url`);
      if (r.data?.url) window.open(r.data.url, "_blank", "noopener");
      else toast.error("Tautan PDF tidak tersedia");
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal membuka PDF")); }
  };

  return (
    <div className="space-y-6" data-testid="papan-kontrol-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Papan Kontrol</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">Penyimpangan prosedur & data yang perlu ditindak. <Hint>Keterlambatan tidak dicatat di sini (itu alarm produksi). Temuan ditutup dengan alasan, tidak dihapus.</Hint></p>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant="destructive" data-testid="pk-merah">MERAH {data.terbuka_merah}</Badge>
          <Badge variant="outline" data-testid="pk-kuning">KUNING {data.terbuka_kuning}</Badge>
          {isOwner && <Button size="sm" variant="outline" onClick={periksaSekarang} disabled={memeriksa} data-testid="pk-periksa">{memeriksa ? "Memeriksa…" : "Periksa sekarang"}</Button>}
        </div>
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari di ringkasan temuan" testId="pk-palang"
                      ekspor={{ nama: "temuan_papan_kontrol", kolom: KOLOM_CSV, ambilHalaman }}>
          <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                  className="h-9 border border-input rounded-md bg-background px-2 text-sm" aria-label="Saring status" data-testid="pk-filter-status">
            <option value="TERBUKA">Terbuka</option><option value="DITUTUP">Ditutup</option><option value="">Semua status</option>
          </select>
          <select value={daftar.nilaiSaringan.warna} onChange={(e) => daftar.setSaringan("warna", e.target.value)}
                  className="h-9 border border-input rounded-md bg-background px-2 text-sm" aria-label="Saring warna" data-testid="pk-filter-warna">
            <option value="">Semua warna</option><option value="MERAH">Merah</option><option value="KUNING">Kuning</option>
          </select>
          <select value={daftar.nilaiSaringan.jenis} onChange={(e) => daftar.setSaringan("jenis", e.target.value)}
                  className="h-9 border border-input rounded-md bg-background px-2 text-sm" aria-label="Saring jenis" data-testid="pk-filter-jenis">
            <option value="">Semua jenis</option>
            {Object.entries(LABEL_JENIS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </PalangDaftar>
        {loading && daftar.tampil.length === 0 ? <p className="text-sm text-muted-foreground">Memuat…</p> : daftar.tampil.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="pk-kosong">
            {daftar.cari.trim() ? "Tidak ada temuan yang cocok dengan pencarian ini." : `Tidak ada temuan ${status === "TERBUKA" ? "terbuka" : ""}.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <ThUrut daftar={daftar} kunci="warna" className="py-2 px-2">Warna</ThUrut>
                  <ThUrut daftar={daftar} kunci="jenis_temuan" className="px-2">Jenis</ThUrut>
                  <th className="px-2">Ringkasan</th>
                  <ThUrut daftar={daftar} kunci="terdeteksi_at" className="px-2">Terdeteksi</ThUrut>
                  <ThUrut daftar={daftar} kunci="status" className="px-2">Status</ThUrut>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {daftar.tampil.map((t) => (
                  <tr key={t.id} className="border-b border-border align-top" data-testid={`pk-temuan-${t.id}`}>
                    <td className="py-2 px-2">{t.warna === "MERAH" ? <Badge variant="destructive">MERAH</Badge> : <Badge variant="outline">KUNING</Badge>}</td>
                    <td className="px-2 whitespace-nowrap">{LABEL_JENIS[t.jenis_temuan] || t.jenis_temuan}</td>
                    <td className="px-2">{t.ringkasan}</td>
                    <td className="px-2 whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(t.terdeteksi_at)}</td>
                    <td className="px-2 whitespace-nowrap">{t.status === "DITUTUP" ? <span className="text-xs">Ditutup · {labelAlasan(t)}{t.catatan_tutup ? ` — ${t.catatan_tutup}` : ""}</span> : "Terbuka"}</td>
                    <td className="px-2 text-right">
                      {bolehTutup && t.status === "TERBUKA" && (
                        <Button size="sm" variant="outline" onClick={() => setTutup(t)} data-testid={`pk-tutup-${t.id}`}>Tutup</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="pk-paginasi" />
      </Card>

      <Card className="p-4" data-testid="pk-ringkasan-harian">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-base font-semibold">Ringkasan harian <span className="text-sm font-normal text-muted-foreground">(14 hari terakhir)</span></h2>
          <Hint>
            Dibuat pemeriksa tiap malam per region; alasan penutupan ikut tampil.
            {ringkasan.umur_pdf_hari ? ` PDF dihapus otomatis sesudah ${ringkasan.umur_pdf_hari} hari.` : ""}
          </Hint>
        </div>
        {ringkasan.items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="pk-ringkasan-kosong">Belum ada ringkasan harian — putaran malam pertama belum berjalan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><th className="text-left py-2 px-2">Tanggal</th><th className="text-left px-2">Merah</th><th className="text-left px-2">Kuning</th><th className="text-left px-2">PDF</th></tr>
              </thead>
              <tbody>
                {ringkasan.items.map((r) => (
                  <tr key={r.id} className="border-b border-border" data-testid={`pk-ringkasan-${r.tanggal_lokal}`}>
                    <td className="py-2 px-2 whitespace-nowrap">{r.tanggal_lokal}</td>
                    <td className="px-2">{r.jumlah_merah}</td>
                    <td className="px-2">{r.jumlah_kuning}</td>
                    <td className="px-2">
                      {r.pdf_document_id ? (
                        <Button size="sm" variant="outline" onClick={() => bukaPdf(r.pdf_document_id)} data-testid={`pk-ringkasan-pdf-${r.tanggal_lokal}`}>Buka PDF</Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.pdf_expires_at ? "PDF sudah lewat umur" : "PDF belum dibuat"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {tutup && <TutupDialog temuan={tutup} alasan={alasan} onClose={() => setTutup(null)} onDone={() => { setTutup(null); load(); }} />}
    </div>
  );
}

function TutupDialog({ temuan, alasan, onClose, onDone }) {
  const [kode, setKode] = useState(alasan[0]?.code || "");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const simpan = async () => {
    setBusy(true);
    try {
      await api.post(`/papan-kontrol/temuan/${temuan.id}/tutup`, { alasan_code: kode, catatan: catatan || null });
      toast.success("Temuan ditutup"); onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menutup temuan")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pk-tutup-dialog">
        <DialogHeader><DialogTitle>Tutup temuan</DialogTitle></DialogHeader>
        <p className="text-sm">{temuan.ringkasan}</p>
        <div className="space-y-3 py-2">
          <div>
            <Label>Alasan (dari daftar)</Label>
            <select value={kode} onChange={(e) => setKode(e.target.value)} className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="pk-tutup-alasan">
              {alasan.map((a) => <option key={a.code} value={a.code}>{a.label_id}</option>)}
            </select>
          </div>
          <div>
            <Label>Catatan{kode === "LAINNYA" ? " (wajib, minimal 10 huruf)" : " (opsional)"}</Label>
            <Textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} rows={3} data-testid="pk-tutup-catatan" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={simpan} disabled={busy || !kode} data-testid="pk-tutup-simpan">Tutup temuan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
